import { useState, useEffect, Fragment } from 'react'
import { Form } from 'react-final-form'
import { FormattedMessage } from 'react-intl'
import { FormInput, FormPhone } from '../forms/final-form-fields'
import { PadiModal } from '../helpers'
import { messages } from '../checkout-adventures/user-forms/ProfileInfo'
import { getUserData, getTotalGuests, connectCaptcha, makeKey } from '../../utils'
import API from 'api'
import store from 'store2'
import Loader from '../helpers/Loader'
import { trackEvent } from 'gtm'
import { intentPopupValidation } from './validation'
import { toast } from 'react-toastify'
import { formatDate } from '../helpers/DateFormat'
import PropTypes from 'prop-types'
import { useHookWithRefCallback } from '../../hooks/useHookWithRefCallback'

const trackClickButton = (padiCheckoutName) => {
  window?.dataLayer.push({
    event: 'promo_view',
    promotion_name: 'Travel - Vacations - Exit Intent',
    creative_name: 'Wait',
  })
}

const intitalValues = getUserData()

const Spots = ({
  data,
  cabinTitle,
}) => {
  return (
    <Fragment>
      <strong>{cabinTitle}:</strong>&nbsp;
      {data.qty} x&nbsp;
      {data.isDiver ? (
        <FormattedMessage id='diver' defaultMessage='Diver' />
      ) : (
        <FormattedMessage id='non_diver' defaultMessage='Non diver' />
      )}
      <br />
    </Fragment>
  )
}

export default function IntentPopup({
  shopTitle,
  roomTitle,
  isLiveabord,
  dateFrom,
  dateTo,
  itineraryTitle,
  guestsSplitting,
  dueNowData,
  countryCode,
}) {
  const [isLoading, setLoading] = useState(false)
  const [isVisible, setVisibility] = useState(false)
  const [captchaId, setCaptchaId] = useState(null)
  const [, setRef] = useHookWithRefCallback(
    () => connectCaptcha('popup-captcha', countryCode, setCaptchaId))

  useEffect(() => {
    const listner = (e) => {
      if(e.clientY < 0 && !store.session.get('exitPopup')) {
        window?.dataLayer.push({
          event: 'promo_view',
          promotion_name: 'Travel - Vacations - Exit Intent',
          creative_name: 'Wait',
        })
        setVisibility(true)
        store.session.set('exitPopup', true)
      }
    }
    document.addEventListener('mouseleave', listner)

    return () => document.removeEventListener('mouseleave', listner)
  }, [])

  const onClose = () => setVisibility(false)
  const showChat = () => {
    trackClickButton()
    const chat = document.getElementById('livechat')
    if(chat) {
      chat.click()
    }
  }

  const padiCheckoutName = isLiveabord ? 'Travel PADI - Liveaboard' : 'Travel PADI - Dive Resort'

  const success = () => {
    setLoading(false)
    toast.success(
      <FormattedMessage
        id='intent.success'
        defaultMessage='Thanks! Your request was successfully submitted'
      />,
    )
    trackEvent('booking_enquiry_reserve_spot_completed')
    trackEvent('checkout_block_spot')
    setVisibility(false)
  }
  const blockSpot = (values) => {
    const captcha = window.grecaptcha && window.grecaptcha.getResponse(captchaId)
    if(!captcha) {
      return toast.error('Captcha is required', { position: 'top-center' })
    }
    const recaptcha = makeKey(captcha)
    trackClickButton(padiCheckoutName)
    setLoading(true)
    const blockRequestData = {
      ...values,
      ...dueNowData,
      recaptcha,
      isResort: true,
    }

    API('checkout/block/')
      .post(blockRequestData)
      .then(() => {
        success()
        window.dataLayer.push({ event: 'block_my_spot_sent' })
      })
      .catch(() => {
        setLoading(false)
      }).finally(() => { window.grecaptcha.reset(captchaId) })
  }

  return (
    <PadiModal
      isOpen={isVisible}
      onClose={onClose}
      className='checkout-exit-popup'
    >
      <Loader isLoading={isLoading} />
      <div className='img-block'>
        <img src={`${window.staticPath}travel_dist/images/wait-icon.gif`} />
      </div>
      <div className='main-block'>
        <div>
          <FormattedMessage
            id='intent.header'
            tagName='h2'
            defaultMessage='There are only a few places left for this trip'
          />
          <FormattedMessage
            id='intent.header2'
            tagName='h3'
            defaultMessage='Would you like us to block a spot for you?'
          />
          <FormattedMessage
            id='intent.text1'
            tagName='p'
            defaultMessage='This will ensure nobody else can "steal" it for 48 hours :)'
          />
          <FormattedMessage
            id='intent.text2'
            tagName='p'
            defaultMessage='No commitment, your spot will be automatically released if you don`t make a final booking by then. We just need the following information from you:'
          />
        </div>

        <Form
          onSubmit={blockSpot}
          subscription={{
            values: true,
          }}
          validate={intentPopupValidation}
          initialValues={intitalValues}
          render={({
            handleSubmit,
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <div className='main-form'>
                  <div className='form-row'>
                    <FormInput
                      name='firstName'
                      label={messages.firstName}
                      isRequired
                      className='form-group'
                    />
                    <FormInput
                      name='lastName'
                      isRequired
                      label={messages.lastName}
                      className='form-group'
                    />
                  </div>
                  <div className='form-row'>
                    <FormInput
                      name='email'
                      isRequired
                      label={messages.email}
                      className='form-group'
                    />
                    <FormPhone
                      name='phone'
                      isRequired
                      label={messages.phone}
                      className='form-group'
                      countryCode={countryCode}
                    />
                  </div>
                  <div className='group-row mtm'>
                    {isLiveabord ? (
                      <ul className='information'>
                        <li>
                          <FormattedMessage
                            id='vessel_name'
                            defaultMessage='Vessel name'
                            tagName='strong'
                          />
                          :&nbsp;
                          <span className='shop-title'>{shopTitle}</span>
                        </li>
                        <li>
                          <FormattedMessage
                            id='itinerary'
                            defaultMessage='Itinerary'
                            tagName='strong'
                          />
                          :&nbsp;
                          <span className='shop-title'>{itineraryTitle}</span>
                        </li>
                        <li>
                          <FormattedMessage
                            id='trip_dates'
                            defaultMessage='Trip dates'
                            tagName='strong'
                          />
                          :&nbsp;
                          <span className='shop-title'>
                            {formatDate(dateFrom)} - {formatDate(dateTo)}
                          </span>
                        </li>
                        <li>
                          <FormattedMessage
                            id='number_of_guests'
                            defaultMessage='Number of guests'
                            tagName='strong'
                          />
                          :&nbsp;
                          <span>{getTotalGuests(dueNowData?.roomParams)}</span>
                        </li>
                        <li>
                          <FormattedMessage
                            id='desired_spot'
                            defaultMessage='Desired spot'
                            tagName='strong'
                          />
                          :&nbsp;
                          <span>
                            {guestsSplitting.map((cabin) => {
                              return (
                                <p>
                                  {cabin.pricing?.map((spotsData) => (
                                    <Spots
                                      cabinTitle={cabin.title}
                                      data={spotsData}
                                    />
                                  ))}
                                </p>
                              )
                            })}
                          </span>
                        </li>
                      </ul>
                    ) : (
                      <ul className='information'>
                        <li>
                          <FormattedMessage
                            id='resort_name'
                            defaultMessage='Resort name'
                            tagName='strong'
                          />
                          :&nbsp;
                          <span className='shop-title'>{shopTitle}</span>
                        </li>
                        <li>
                          <FormattedMessage
                            id='room'
                            defaultMessage='Room'
                            tagName='strong'
                          />
                          :&nbsp;
                          <span>{roomTitle}</span>
                        </li>
                        <li>
                          <FormattedMessage
                            id='number_of_guests'
                            defaultMessage='Number of guests'
                            tagName='strong'
                          />
                          :&nbsp;
                          <span>{getTotalGuests(dueNowData?.roomParams)}</span>
                        </li>
                      </ul>
                    )}
                  </div>
                  <div className='group-row mtm'>
                    <div ref={setRef} id='popup-captcha' />
                  </div>
                  <button className='btn-red'>
                    <FormattedMessage
                      id='block_my_spots'
                      defaultMessage='Block my spots'
                    />
                  </button>
                </div>
              </form>
            )
          }}
        />
        <div className='or'>
          <FormattedMessage id='or' defaultMessage='OR' tagName='h3' />
        </div>
        <div className='group-row'>
          <div>
            <FormattedMessage
              id='intent.text3'
              tagName='h4'
              defaultMessage='Would you prefer to talk to one of our scuba travel experts (we are available 24/7)?'
            />
          </div>
          <button className='btn-red-invert' onClick={showChat}>
            <FormattedMessage
              id='start_chatting'
              defaultMessage='Start chatting'
            />
          </button>
        </div>
      </div>
    </PadiModal>
  )
}

IntentPopup.propTypes = {
  shopTitle: PropTypes.string,
  roomTitle: PropTypes.string,
  isLiveabord: PropTypes.bool,
  dateFrom: PropTypes.string,
  dateTo: PropTypes.string,
  itineraryTitle: PropTypes.string,
  guestsSplitting: PropTypes.array,
  dueNowData: PropTypes.object,
  countryCode: PropTypes.string,
}

Spots.propTypes = {
  cabinTitle: PropTypes.string,
  data: PropTypes.object,
}
